import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { ApolloError } from "@apollo/client";
import { CityWithRegions, ScreenResult } from "../schemas/screenSchema";
import { ScreenFilters, screenService } from "../api/services/screenService";

// Utility function to extract error message from Apollo errors
const getErrorMessage = (error: unknown): string => {
  if (error instanceof ApolloError) {
    // Check for GraphQL errors first
    if (error.graphQLErrors && error.graphQLErrors.length > 0) {
      return error.graphQLErrors[0].message || error.message;
    }
    // Check for network errors
    if (error.networkError) {
      return error.networkError.message || "Network error occurred";
    }
    // Fall back to error message
    return error.message;
  }
  if (error instanceof Error) {
    return error.message;
  }
  return "An unexpected error occurred";
};

export interface CityWithRegionsFilters {
  search?: string;
  orientation?: string;
}

export const useScreens = (
  filters: ScreenFilters = {},
  cityWithRegionsFilters?: CityWithRegionsFilters,
  options?: { enabled?: boolean },
) => {
  const queryClient = useQueryClient();
  const enabled = options?.enabled !== false;

  const {
    data: screens,
    isLoading: isScreensLoading,
    refetch: refetchScreens,
  } = useQuery<ScreenResult, Error>({
    queryKey: ["screens", filters],
    queryFn: () => screenService.getAll(filters),
    enabled,
    // Bounds status-badge staleness when the screenStatusChanged WebSocket
    // is silently dead (see useScreensInfinite for the same safety net).
    refetchInterval: 60_000,
  });

  const {
    data: cityWithRegions,
    isLoading: isCityWithRegionsLoading,
    refetch: refetchCityWithRegions,
  } = useQuery<CityWithRegions[], Error>({
    queryKey: ["screens-by-city-regions", cityWithRegionsFilters],
    queryFn: () =>
      screenService.getScreensByCityWithRegions(cityWithRegionsFilters),
    enabled,
  });

  const createScreenMutation = useMutation({
    mutationFn: screenService.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["screens"] });
      queryClient.invalidateQueries({ queryKey: ["screens-by-city-regions"] });
      toast.success("Screen created successfully");
    },
    onError: (error: unknown) => {
      const errorMessage = getErrorMessage(error);
      toast.error(errorMessage || "Failed to create screen");
    },
  });

  const updateScreenMutation = useMutation({
    mutationFn: screenService.update,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["screens"] });
      queryClient.invalidateQueries({ queryKey: ["screens-by-city-regions"] });
      toast.success("Screen updated successfully");
    },
    onError: (error: unknown) => {
      const errorMessage = getErrorMessage(error);
      toast.error(errorMessage || "Failed to update screen");
    },
  });

  const resetScreenMutation = useMutation({
    mutationFn: screenService.reset,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["screens"] });
      toast.success("Screen reset triggered successfully");
    },
    onError: (error: unknown) => {
      const errorMessage = getErrorMessage(error);
      toast.error(errorMessage || "Failed to reset screen");
    },
  });

  const deleteScreenMutation = useMutation({
    mutationFn: screenService.remove,
    onSuccess: (_data, ids) => {
      queryClient.invalidateQueries({ queryKey: ["screens"] });
      queryClient.invalidateQueries({ queryKey: ["screens-by-city-regions"] });
      if (Array.isArray(ids) && ids.length > 1) {
        toast.success(`${ids.length} items deleted successfully`);
        return;
      }
      toast.success("Screen deleted successfully");
    },
    onError: (error: unknown) => {
      const errorMessage = getErrorMessage(error);
      toast.error(errorMessage || "Failed to delete screen");
    },
  });

  return {
    screens,
    refetchScreens,
    isScreensLoading,
    cityWithRegions,
    refetchCityWithRegions,
    isCityWithRegionsLoading,
    createScreen: createScreenMutation.mutateAsync,
    updateScreen: updateScreenMutation.mutateAsync,
    resetScreen: resetScreenMutation.mutateAsync,
    deleteScreens: deleteScreenMutation.mutate,
  };
};
